import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, App } from 'antd';
import {
  fetchProducts,
  fetchCompanyInfo,
  fetchHomeData,
  fetchActivities,
  fetchPaidServices,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import Carousel from '../../components/Carousel/Carousel';
import './Home.scss';

// 大屏轮播幻灯片（真实文案可走后台 /off/home 的 banner 字段替换）
// 注：「尊享付费服务」已调整至第一页
const SLIDES = [
  {
    title: '尊享付费服务',
    subtitle: '三大增值服务 · 会员专享',
    desc: '软装全案设计 ¥199、私享家居管家 ¥299、大师私定制 ¥599，为您的理想家再添一份省心。',
    bg: 'linear-gradient(135deg, #fbeec2 0%, #f3d98a 55%, #ecd28f 100%)',
    cta: { text: '查看尊享服务', scrollTo: 'paid-section' },
  },
  {
    title: '勘察服务',
    subtitle: '上门勘验 · 专业报告',
    desc: '专业团队上门对房屋装修现场进行全面勘验，出具详细勘察报告，为您的装修决策提供专业依据。收费服务，预约后工程师将上门服务。',
    bg: 'linear-gradient(135deg, #1f6b47 0%, #2d8a5e 100%)',
    theme: 'dark',
    cta: { text: '了解勘察服务', to: '/services' },
  },
  {
    title: '好家好美意',
    subtitle: '装点每一个家',
    desc: '家居销售 · 整体家装 · 软装搭配，一站式家居解决方案，让每个家庭轻松拥有理想的家。',
    bg: 'linear-gradient(120deg, #f0faf5 0%, #e2f0e8 55%, #d4ead3 100%)',
    cta: { text: '浏览产品服务', to: '/services' },
  },
  {
    title: '免费预约 专属顾问',
    subtitle: '一对一服务',
    desc: '免费量房、免费设计、免费专属咨询，专业团队为您量身定制。',
    bg: 'linear-gradient(135deg, #1f6b47 0%, #2d8a5e 100%)',
    theme: 'dark',
    cta: { text: '立即预约咨询', to: '/contact' },
  },
];

const ADVANTAGES = [
  { icon: '✨', title: '一站式服务', desc: '从家具选购到整体家装，一站解决所有家居需求' },
  { icon: '🛡️', title: '品质保障', desc: '严选环保材料与认证家居，标准化施工，品质看得见' },
];

const ACT_CAT_LABELS = {
  all: '全部',
  promo: '限时特惠',
  new: '新品发布',
  store: '到店活动',
  member: '会员专享',
};

function Home() {
  const navigate = useNavigate();
  const { user, addPaidService } = useAuth();
  const { message } = App.useApp();
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState([]);
  const [activities, setActivities] = useState([]);
  const [paidServices, setPaidServices] = useState([]);
  const [actCat, setActCat] = useState('all');

  // 公司活动分类：仅保留有活动的分类（清理无活动的空分类），「全部」始终保留
  const actCats = [
    { key: 'all', label: '全部' },
    ...Array.from(new Set(activities.map((a) => a.category)))
      .map((k) => ({ key: k, label: ACT_CAT_LABELS[k] || k }))
      .filter((c) => c.label),
  ];

  useEffect(() => {
    let mounted = true;
    Promise.all([
      fetchProducts('all'),
      fetchCompanyInfo(),
      fetchHomeData(),
      fetchActivities(),
      fetchPaidServices(),
    ]).then(([productList, company, home, actList, paidList]) => {
      if (!mounted) return;
      setProducts(productList.slice(0, 3));
      setStats((home && home.stats) || (company && company.stats) || []);
      setActivities(actList || []);
      setPaidServices(paidList || []);
    });
    return () => {
      mounted = false;
    };
  }, []);

  // 认购付费尊享服务（未登录跳转登录页，登录后回到首页继续认购）
  const handleBuy = (s) => {
    if (!user) {
      navigate('/login?redirect=/');
      return;
    }
    addPaidService({
      id: s.id,
      name: s.name,
      icon: s.icon,
      price: s.price,
      period: s.period,
      desc: s.desc,
    });
    message.success(`已认购「${s.name}」，可在会员中心查看`);
  };

  const filteredActs =
    actCat === 'all'
      ? activities
      : activities.filter((a) => a.category === actCat);

  return (
    <div className="home">
      {/* 大屏轮播 */}
      <Carousel slides={SLIDES} />

      {/* 数据统计 */}
      <section className="stats-bar">
        <div className="container stats-inner">
          {stats.map((s) => (
            <div className="stat-item" key={s.label}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 付费尊享服务（已提升至「为什么选择好美意」上方） */}
      <section className="section section-paid" id="paid-section">
        <div className="container">
          <div className="paid-head">
            <div>
              <h2 className="section-title left">尊享付费服务</h2>
              <p className="section-sub left">专业增值服务，为您的理想家再添一份省心</p>
            </div>
            <span className="paid-badge">付费 · 会员专享</span>
          </div>
          <div className="paid-grid">
            {paidServices.map((s) => (
              <div className="paid-card" key={s.id}>
                <div className="paid-icon">{s.icon}</div>
                <h3>{s.name}</h3>
                <div className="paid-price">
                  <strong>¥{s.price}</strong>
                  <span>/{s.period}</span>
                </div>
                <p className="paid-desc">{s.desc}</p>
                <ul className="paid-feats">
                  {s.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <Button type="primary" block className="paid-buy-btn" onClick={() => handleBuy(s)}>
                  立即认购
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 核心优势 */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">为什么选择好美意</h2>
          <p className="section-sub">多年深耕家装消费领域，值得信赖的选择</p>
          <div className="advantage-grid">
            {ADVANTAGES.map((a) => (
              <div className="advantage-card" key={a.title}>
                <div className="advantage-icon">{a.icon}</div>
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 热门服务 */}
      <section className="section section-gray">
        <div className="container">
          <h2 className="section-title">热门产品服务</h2>
          <p className="section-sub">家居与生活服务，满足全场景需求</p>
          <div className="product-grid">
            {products.map((p) => (
              <Link
                to={`/services/${p.id}`}
                className="product-card product-link"
                key={p.id}
              >
                <div className="product-icon">{p.icon}</div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <div className="product-tags">
                  {p.features.map((f) => (
                    <span key={f}>{f}</span>
                  ))}
                </div>
                <span className="product-more">查看详情 →</span>
              </Link>
            ))}
          </div>
          <div className="center">
            <Link to="/services" className="btn btn-outline">
              查看全部服务 →
            </Link>
          </div>
        </div>
      </section>

      {/* 公司活动 */}
      <section className="section">
        <div className="container">
          <h2 className="section-title">公司活动</h2>
          <p className="section-sub">最新优惠与会员福利，不容错过</p>
          <div className="filter-tabs">
            {actCats.map((c) => (
              <button
                key={c.key}
                className={actCat === c.key ? 'tab active' : 'tab'}
                onClick={() => setActCat(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="activity-grid">
            {filteredActs.map((a) => (
              <div className="activity-card" key={a.id}>
                <div className="activity-top">
                  <span className="activity-icon">{a.icon}</span>
                  <span className="activity-tag">{a.tag}</span>
                </div>
                <h3>{a.title}</h3>
                <p>{a.desc}</p>
                <span className="activity-date">📅 {a.date}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta">
        <div className="container cta-inner">
          <h2>准备好开启理想家了吗？</h2>
          <p>专业顾问一对一服务，免费量房、免费设计、免费专属咨询</p>
          <button
            className="btn btn-light"
            onClick={() => navigate('/contact')}
          >
            立即预约
          </button>
        </div>
      </section>
    </div>
  );
}

export default Home;
